import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search, ShieldAlert, X } from "lucide-react";
import { UnassignedDiagnosis } from "../components/UnassignedDiagnosis";
import { SearchableSelect } from "@/components/SearchableSelect";
import { ExportButtons } from "@/components/ExportButtons";
import { CustomAgeingCompare } from "@/components/CustomAgeingCompare";
import { UnlinkedPayments } from "@/components/UnlinkedPayments";
import { DateFilters, filterParams } from "@/components/DateFilters";
import { useAgeingBasis, AgeingBasisToggle } from "@/hooks/useAgeingBasis";
import { flattenTree, NodeRow, DetailRow } from "@/components/AgeingTree";
import { FinancingSummary } from "@/components/FinancingSummary";
import { TBReconciliation } from "@/components/TBReconciliation";
import { AgeingTBTie } from "@/components/AgeingTBTie";
import { BadDatesBanner } from "@/components/BadDatesBanner";
import { API } from "@/context/AuthContext";
import { inr, inrShort } from "@/pages/Dashboard";

const BUCKET_COLORS = ["#10B981", "#0EA5E9", "#F59E0B", "#F97316", "#EF4444", "#991B1B"];
const CATEGORIES = ["Balance", "Retention", "Advance"];
const AP_CATEGORIES = [...CATEGORIES, "PDC / Deposits"];
const catSlug = (c) => c.toLowerCase().replace(/[^a-z]+/g, "-");

export function AgeingReport({ title, subtitle, endpoint, exportKey, drillOptions, entityLabel, testPrefix, partyType, partyLabel }) {
  const isAP = partyType === "ap";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [drill, setDrill] = useState(drillOptions[0].value);
  const [filters, setFilters] = useState({ asOn: "today", from: null, to: null });
  const [basis, changeBasis] = useAgeingBasis();
  const [category, setCategory] = useState("all");
  const [currency, setCurrency] = useState(() => (searchParams.get("currency") || "").toUpperCase());
  const [project, setProject] = useState(() => {
    const p = searchParams.get("project");
    return p ? p.split(",").filter(Boolean) : [];
  });
  const [party, setParty] = useState([]);
  const [finMonth, setFinMonth] = useState("");
  const [msme, setMsme] = useState("all");
  const [msmeOverdue, setMsmeOverdue] = useState(false);
  const [bucket, setBucket] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [parties, setParties] = useState([]);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [exportParams, setExportParams] = useState({});

  useEffect(() => {
    if (!partyType) return;
    axios.get(`${API}/ledger/${partyType}/parties`).then((r) => setParties(r.data)).catch(() => {});
  }, [partyType]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const load = useCallback(() => {
    const params = {
      drill,
      ...(category !== "all" ? { category } : {}),
      ...(project.length ? { project: project.join(",") } : {}),
      ...(party.length ? { party: party.join(",") } : {}),
      ...(isAP && msme !== "all" ? { msme } : {}),
      ...(isAP && msmeOverdue ? { msme_overdue: true } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(bucket ? { bucket } : {}),
      ...(currency ? { currency } : {}),
      ...(isAP && finMonth ? { fin_month: finMonth } : {}),
      ...filterParams(filters),
      basis,
    };
    setExportParams(params);
    axios.get(`${API}${endpoint}`, { params }).then((r) => {
      setData(r.data);
      // When searching, auto-expand the branches so matching invoices are visible immediately.
      if (search.trim()) {
        const ids = new Set();
        const walk = (nodes, prefix = "") => (nodes || []).forEach((n, i) => {
          const id = prefix ? `${prefix}.${i}` : `${i}`;
          if (n.children && n.children.length) { ids.add(id); walk(n.children, id); }
        });
        walk(r.data.tree);
        setExpanded(ids);
      } else {
        setExpanded(new Set());
      }
    }).catch(() => {});
  }, [drill, filters, category, project, party, msme, msmeOverdue, search, bucket, isAP, endpoint, currency, finMonth, basis]);

  const side = isAP ? "ap" : "ar";
  const [tbData, setTbData] = useState(null);
  const [ko, setKo] = useState(null);
  useEffect(() => {
    if (!isAP) return;
    setKo(null);
    axios.get(`${API}/reports/ap-ageing/knockoff`, { params: filters.asOn && filters.asOn !== "today" ? { as_on: filters.asOn } : {} })
      .then((r) => setKo(r.data.by_invoice)).catch(() => setKo({}));
  }, [isAP, filters.asOn]);
  const [qu, setQu] = useState(null);
  const [tbVer, setTbVer] = useState(0);
  useEffect(() => {
    setQu(null);
    axios.post(`${API}/reports/${side}-ageing/quick-update`)
      .then((r) => { setQu(r.data); if (!r.data.skipped && (r.data.added || r.data.cleared || r.data.updated)) { load(); setTbVer((v) => v + 1); } })
      .catch((e) => setQu({ error: e.response?.data?.detail || "SAP not reachable" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side]);

  useEffect(() => {
    load();
    window.addEventListener("sap-synced", load);
    return () => window.removeEventListener("sap-synced", load);
  }, [load]);

  if (!data) return <div className="animate-pulse text-slate-400 text-sm">Loading report...</div>;

  const chartData = data.buckets.map((b, i) => ({ bucket: b, amount: data.summary[b], fill: BUCKET_COLORS[i] }));
  const visibleRows = flattenTree(data.tree, expanded);
  const toggleBucket = (b) => setBucket((cur) => (cur === b ? null : b));
  const openLedger = (node) => {
    const p = new URLSearchParams({ party: node.party_code });
    if (bucket) p.set("bucket", bucket);
    if (category !== "all") p.set("category", category);
    navigate(`/${partyType}-ledger?${p.toString()}`);
  };

  return (
    <div className="space-y-6" data-testid={`${testPrefix}-page`}>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500 mt-1">{subtitle} · ageing by <b data-testid={`${testPrefix}-basis-label`}>{basis === "invoice" ? "invoice date" : "due date (SAP net due date)"}</b>{basis === "invoice" ? " — 'Not Due' then only holds future-dated documents" : ""}</p>
        </div>
        {isAP && <UnlinkedPayments asOn={filters.asOn} testPrefix={testPrefix} />}
        {isAP && <CustomAgeingCompare testPrefix={testPrefix} />}
        <ExportButtons report={exportKey} endpoint={`${endpoint}/export`} params={exportParams}
          extra={partyType === "ar" ? [{ fmt: "xlsx_old", label: "Excel (old layout)", title: "Previous AR ageing Excel layout" }] : []} />
      </div>

      <AgeingTBTie data={tbData} qu={qu} testPrefix={testPrefix} />
      <BadDatesBanner side={side} testPrefix={testPrefix} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <DateFilters showAsOn filters={filters} setFilters={setFilters} testPrefix={testPrefix} />
          <AgeingBasisToggle basis={basis} onChange={changeBasis} testPrefix={testPrefix} />
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") setSearch(searchInput); }}
              placeholder={isAP ? "Exact SAP ref / supplier inv no — use * for wildcard" : "Exact SAP ref / invoice no — use * for wildcard"}
              className="w-64 h-9 pl-8 pr-8 border-slate-300 text-sm bg-white"
              data-testid={`${testPrefix}-search-input`}
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => { setSearchInput(""); setSearch(""); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                data-testid={`${testPrefix}-search-clear`}
              >✕</button>
            )}
          </div>
          <SearchableSelect
            multiple
            options={parties.map((p) => ({ value: p.code, label: p.name, sublabel: p.code }))}
            value={party}
            onChange={setParty}
            allLabel={`All ${partyLabel}s`}
            placeholder={`All ${partyLabel}s`}
            width="w-64"
            testId={`${testPrefix}-party-select`}
          />
          <SearchableSelect
            multiple
            options={(data.projects || []).map((p) => ({ value: p.code, label: p.name || p.code, sublabel: p.code }))}
            value={project}
            onChange={setProject}
            allLabel="All Projects"
            placeholder="All Projects"
            width="w-52"
            allowPattern
            testId={`${testPrefix}-project-select`}
          />
          {isAP && (
            <Select value={msme} onValueChange={setMsme}>
              <SelectTrigger className="w-40 h-9 border-slate-300 text-sm bg-white" data-testid={`${testPrefix}-msme-select`}>
                <SelectValue placeholder="MSME: All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">MSME: All</SelectItem>
                <SelectItem value="Yes">MSME: Yes</SelectItem>
                <SelectItem value="No">MSME: No</SelectItem>
              </SelectContent>
            </Select>
          )}
          {isAP && (
            <button
              type="button"
              onClick={() => setMsmeOverdue((v) => !v)}
              data-testid={`${testPrefix}-msme-overdue-toggle`}
              className={`h-9 px-3 rounded-md text-sm font-semibold border transition-colors duration-200 flex items-center gap-1.5 ${msmeOverdue ? "bg-red-600 border-red-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-red-300 hover:text-red-600"}`}
            >
              <ShieldAlert className="h-4 w-4" /> MSME &gt; 45 days
            </button>
          )}
        </div>
        <Tabs value={category} onValueChange={setCategory}>
          <TabsList>
            <TabsTrigger value="all" data-testid={`${testPrefix}-category-all`}>All</TabsTrigger>
            {(isAP ? AP_CATEGORIES : CATEGORIES).map((c) => (
              <TabsTrigger key={c} value={c} data-testid={`${testPrefix}-category-${catSlug(c)}`}>{c}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {(currency || data.fx_summary?.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`${testPrefix}-currency-bar`}>
          <span className="text-slate-500">Currency:</span>
          {[["", "All"], ["INR", "INR only"], ["FX", "Other currencies"]].map(([v, l]) => (
            <button key={v || "all"} type="button" onClick={() => setCurrency(v)} data-testid={`${testPrefix}-currency-${v || "all"}`}
              className={`px-2.5 py-1 rounded-full border transition-colors ${currency === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>{l}</button>
          ))}
          {currency && !["INR", "FX"].includes(currency) && <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-200 font-semibold">{currency} only</span>}
          {data.fx_summary?.length > 0 && <span className="text-slate-400">· shown total {inrShort(data.total)} = INR {inrShort(data.inr_summary?.total)} + {data.fx_summary.map((f) => `${f.currency} ${Number(f.amount_fc).toLocaleString("en-IN", { maximumFractionDigits: 0 })} (≈ ${inrShort(f.total)})`).join(" + ")}</span>}
        </div>
      )}

      <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 ${isAP ? "lg:grid-cols-4" : ""}`}>
        {(isAP ? AP_CATEGORIES : CATEGORIES).map((c) => {
          const s = data.category_summary?.[c] || { total: 0, count: 0 };
          const tag = isAP
            ? (c === "Retention" ? "(RT / SGL H)" : c === "Advance" ? "(KZ / SGL A)" : c === "PDC / Deposits" ? "(SGL B / G)" : "(KR / RE)")
            : (c === "Retention" ? "(RT / SGL H)" : c === "Advance" ? "(DZ / SGL A)" : "(RV / DR)");
          const nature = c === "PDC / Deposits" ? "Debit" : isAP ? "Credit" : "Debit";
          return (
            <button
              key={c}
              data-testid={`${testPrefix}-catcard-${catSlug(c)}`}
              onClick={() => setCategory(category === c ? "all" : c)}
              className={`text-left rounded-lg border p-4 bg-white shadow-sm transition-colors duration-200 ${category === c ? "border-sky-600 ring-1 ring-sky-600" : "border-slate-200 hover:border-slate-300"}`}
            >
              <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">{c} {tag} <span className="text-slate-400">· {nature}</span></p>
              <p className="text-xl font-black tracking-tight text-slate-900 mt-1">{inrShort(s.total)}</p>
              <p className="text-xs text-slate-400 mt-0.5">{s.count} invoices{category === c ? " · filtering active" : ""}</p>
              {s.fx?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-dashed border-slate-200 space-y-1" data-testid={`${testPrefix}-catcard-${catSlug(c)}-fx`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">INR invoices</span>
                    <span className="font-semibold text-slate-800 tabular-nums">{inrShort(s.inr.total)} <span className="text-slate-400 font-normal">· {s.inr.count}</span></span>
                  </div>
                  {s.fx.map((f) => (
                    <div key={f.currency} role="button" tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setCategory(c); setCurrency(currency === f.currency && category === c ? "" : f.currency); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setCategory(c); setCurrency(f.currency); } }}
                      className={`flex items-center justify-between text-xs rounded px-1 -mx-1 ${currency === f.currency && category === c ? "bg-amber-100 text-amber-900" : "hover:bg-amber-50"}`}
                      data-testid={`${testPrefix}-catcard-${c.toLowerCase()}-fx-${f.currency}`}>
                      <span className="font-semibold text-amber-800">{f.currency} <span className="font-normal text-slate-500">{f.count} inv</span></span>
                      <span className="tabular-nums"><b>{f.currency} {Number(f.amount_fc).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</b> <span className="text-slate-500">≈ {inrShort(f.total)}</span></span>
                    </div>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {data.unassigned_summary && data.unassigned_summary.count > 0 && (
        <UnassignedDiagnosis s={data.unassigned_summary} testPrefix={testPrefix} isAP={isAP} />
      )}

      {isAP && data.msme_overdue_summary && data.msme_overdue_summary.count > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 fade-in-up" data-testid={`${testPrefix}-msme-overdue-banner`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-red-100 flex items-center justify-center shrink-0">
                <ShieldAlert className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-red-800">MSME dues beyond the 45-day limit</p>
                <p className="text-xs text-red-700/80 mt-0.5">
                  <span className="font-semibold">{inrShort(data.msme_overdue_summary.principal)}</span> principal across {data.msme_overdue_summary.count} invoices ·
                  estimated MSMED Act interest <span className="font-semibold">~{inrShort(data.msme_overdue_summary.interest)}</span>
                  <span className="text-red-600/70"> (compounded monthly @ {data.msme_overdue_summary.penal_rate}% p.a. = 3× {data.msme_overdue_summary.bank_rate}% RBI bank rate)</span>
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setMsmeOverdue((v) => !v)}
              data-testid={`${testPrefix}-msme-overdue-banner-btn`}
              className={`h-9 px-4 rounded-md text-sm font-semibold border transition-colors duration-200 shrink-0 ${msmeOverdue ? "bg-white border-red-300 text-red-700" : "bg-red-600 border-red-600 text-white hover:bg-red-700"}`}
            >
              {msmeOverdue ? "Show all invoices" : "Show only these invoices"}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm lg:col-span-2 fade-in-up">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-slate-800">Ageing Buckets</CardTitle>
            <span className="text-xs text-slate-400">Click a bar to filter the drill-down</span>
          </CardHeader>
          <CardContent>
            <div className="h-64" data-testid={`${testPrefix}-bucket-chart`}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="bucket" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={inrShort} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={80} />
                  <Tooltip formatter={(v) => inr(v)} cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="amount" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(entry) => entry?.bucket && toggleBucket(entry.bucket)}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} fillOpacity={bucket && bucket !== entry.bucket ? 0.25 : 1}
                        stroke={bucket === entry.bucket ? "#0F172A" : "none"} strokeWidth={bucket === entry.bucket ? 2 : 0}
                        data-testid={`${testPrefix}-bar-${i}`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm fade-in-up">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-800">Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-baseline border-b border-slate-100 pb-3">
              <span className="text-sm text-slate-500">Total Outstanding</span>
              <span className="text-xl font-black tracking-tight text-slate-900" data-testid={`${testPrefix}-total`}>{inrShort(data.total)}</span>
            </div>
            {data.buckets.map((b, i) => (
              <button key={b} type="button" onClick={() => toggleBucket(b)} data-testid={`${testPrefix}-summary-bucket-${i}`}
                className={`w-full flex justify-between items-center rounded-md px-2 py-1 -mx-2 transition-colors ${bucket === b ? "bg-slate-900 text-white" : "hover:bg-slate-100"} ${bucket && bucket !== b ? "opacity-50" : ""}`}>
                <span className={`flex items-center gap-2 text-sm ${bucket === b ? "text-white" : "text-slate-600"}`}>
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: BUCKET_COLORS[i] }} />
                  {b}
                </span>
                <span className={`text-sm font-semibold ${bucket === b ? "text-white" : "text-slate-800"}`}>{inrShort(data.summary[b])}</span>
              </button>
            ))}
            <p className="text-xs text-slate-400 pt-2">{data.count} open invoices · {data.line_count} WBS lines{bucket ? ` in ${bucket}` : ""}</p>
            <p className="text-xs text-slate-400">Net of receipts &amp; advances — reconciles with the Ledger closing balance.</p>
          </CardContent>
        </Card>
      </div>

      {isAP && <FinancingSummary summary={data.financing_summary} selected={party} onSelect={setParty} finMonth={finMonth} onSelectMonth={setFinMonth} exportParams={exportParams} testPrefix={testPrefix} />}
      <TBReconciliation side={isAP ? "ap" : "ar"} asOn={filters.asOn} basis={basis} testPrefix={testPrefix} onSelectGroup={() => setDrill("tbgroup")} version={tbVer} onData={(d) => { setTbData(d); if (d?.clearing_check?.healed) load(); }} />

      <Card className="border-slate-200 shadow-sm fade-in-up">
        <CardHeader className="pb-3 flex flex-row items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
              {entityLabel} — Drill-down
              {bucket && (
                <button type="button" onClick={() => setBucket(null)} data-testid={`${testPrefix}-bucket-filter-chip`}
                  className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white text-xs font-medium px-2.5 py-1 hover:bg-slate-700">
                  {category !== "all" ? `${category} · ` : ""}{bucket}
                  <X className="h-3 w-3" />
                </button>
              )}
            </CardTitle>
            <p className="text-xs text-slate-400 mt-0.5">
              {bucket ? `Showing only ${entityLabel.toLowerCase()} with open items in the ${bucket} bracket` : "Amounts with multiple WBS are apportioned by line value"}
            </p>
          </div>
          <Tabs value={drill} onValueChange={setDrill}>
            <TabsList>
              {drillOptions.map((o) => (
                <TabsTrigger key={o.value} value={o.value} data-testid={`${testPrefix}-drill-${o.value}`}>{o.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          <Table data-testid={`${testPrefix}-tree-table`}>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Hierarchy</TableHead>
                {data.buckets.map((b) => (
                  <TableHead key={b} className={`text-xs uppercase tracking-wider text-right ${bucket === b ? "text-slate-900 font-bold bg-amber-50" : "text-slate-500"}`}>{b}</TableHead>
                ))}
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Total</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Docs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.map((r) => (
                r.kind === "node"
                  ? <NodeRow key={r.id} row={r} buckets={data.buckets} expanded={expanded} onToggle={toggle} testPrefix={testPrefix} onLedger={openLedger} ko={isAP ? ko : undefined} />
                  : <DetailRow key={r.id} detail={r.detail} colSpan={data.buckets.length + 3} testPrefix={testPrefix} ko={isAP ? ko : undefined} />
              ))}
              {(!data.tree || data.tree.length === 0) && (
                <TableRow><TableCell colSpan={data.buckets.length + 3} className="text-center text-sm text-slate-400 py-8">No open items for the selected filters</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default function APAgeing() {
  return (
    <AgeingReport
      title="AP Ageing"
      subtitle="Accounts payable ageing — drill down Supplier→WBS→PO→Invoice or WBS→Supplier→PO→Invoice"
      endpoint="/reports/ap-ageing"
      exportKey="ap-ageing"
      drillOptions={[{ value: "supplier", label: "Supplier → WBS → PO → Invoice" }, { value: "wbs", label: "WBS → Supplier → PO → Invoice" }, { value: "tbgroup", label: "TB Group → Supplier → PO → Invoice" }]}
      entityLabel="Vendor Liability"
      testPrefix="ap-ageing"
      partyType="ap"
      partyLabel="Supplier"
    />
  );
}
